import { applySnapshot, getSnapshot, types, flow } from 'mobx-state-tree'
import { AdminModel } from 'stores/models'
import { apiV1 } from '../../utils'

const AboutMeModel = types.model('AboutMeModel', {
  id: types.string,
  bankId: types.maybeNull(types.union(types.string, types.number)),
  email: types.string,
  firstName: types.string,
  lastName: types.string,
  userName: types.string,
  phoneNumber: types.maybeNull(types.string),
  countryId: types.maybeNull(types.union(types.string, types.number)),
})

const AdminsStore = types
  .model('AdminsStore', {
    _data: types.optional(types.array(AdminModel), []),
    _isPending: types.optional(types.boolean, false),
    _aboutMe: types.maybeNull(AboutMeModel),
    _isPendingAboutMe: types.optional(types.boolean, false),
  })
  .actions(self => {
    const API_URL = '/Admins'

    const setAdminsList = adminsList => {
      applySnapshot(self._data, adminsList)
    }

    // Получить всех админов
    const getAdminsList = flow(function* (bankId = null) {
      self._isPending = true
      try {
        const response = yield apiV1.get(`${API_URL}`, { params: { bankId } })
        setAdminsList(response.data)
      } catch (error) {
        console.error('Failed to load admins:', error)
      } finally {
        self._isPending = false
      }
    })

    // Получить информацию о текущем админе
    const getAdminInfo = flow(function* () {
      self._isPendingAboutMe = true
      try {
        const response = yield apiV1.get(`${API_URL}/AboutMe`)
        if (response?.data) {
          self._aboutMe = AboutMeModel.create(response.data)
          return response
        } else {
          self._aboutMe = null
        }
      } catch (error) {
        console.error('Failed to fetch admin info:', error)
        self._aboutMe = null
      } finally {
        self._isPendingAboutMe = false
      }
    })

    // Добавить нового админа
    const addNewAdmin = flow(function* (adminData) {
      try {
        const response = yield apiV1.post(`${API_URL}`, adminData)
        // Можно сразу добавить в _data, если нужно
        self._data.push(response.data)
        return response.data
      } catch (error) {
        console.error('Failed to add admin:', error)
      }
    })

    // Редактировать админа
    const editAdmin = flow(function* (adminData) {
      try {
        const response = yield apiV1.put(`${API_URL}`, adminData)
        // Обновляем локальный массив
        const index = self._data.findIndex(a => a.id === response.data.id)
        if (index >= 0) self._data[index] = response.data
        return response.data
      } catch (error) {
        console.error('Failed to edit admin:', error)
      }
    })

    // Удалить админа
    const deleteAdmin = flow(function* (id) {
      try {
        yield apiV1.delete(`${API_URL}/${id}`, { params: { id } })
        applySnapshot(
          self._data,
          self._data.filter(a => a.id !== id),
        )
      } catch (error) {
        console.error('Failed to delete admin:', error)
      }
    })

    // Сбросить информацию о текущем админе (используется при выходе из системы,
    // чтобы данные предыдущего админа/банка/страны не «утекали» в следующую сессию)
    const clearAboutMe = () => {
      self._aboutMe = null
    }

    return {
      getAdminsList,
      getAdminInfo,
      addNewAdmin,
      editAdmin,
      deleteAdmin,
      clearAboutMe,
    }
  })
  .views(self => ({
    get adminsList() {
      return getSnapshot(self._data)
    },
    get aboutMe() {
      return self._aboutMe ? getSnapshot(self._aboutMe) : null
    },
    get isPending() {
      return self._isPending
    },
    get isPendingAboutMe() {
      return self._isPendingAboutMe
    },
  }))

export default AdminsStore
